<template>
  <div class="login-container">
    <div class="login-content">
      <div class="login-box">
        <div class="title">综合业务系统</div>
        <el-form 
          :model="loginForm" 
          :rules="rules" 
          ref="loginForm" 
          @keyup.enter.native="handleLogin"
          class="login-form"
          status-icon
        >
          <el-form-item prop="username">
            <el-input 
              v-model.trim="loginForm.username" 
              prefix-icon="el-icon-user"
              placeholder="请输入登录ID"
              class="custom-input"
              :show-message="true"
              @focus="clearValidate('username')"
            >
            </el-input>
          </el-form-item>
          <el-form-item prop="password">
            <el-input 
              v-model.trim="loginForm.password" 
              prefix-icon="el-icon-lock"
              type="password"
              placeholder="请输入密码"
              show-password
              class="custom-input"
              :show-message="true"
              @focus="clearValidate('password')"
            >
            </el-input>
          </el-form-item>
          <el-form-item prop="captcha">
            <div class="captcha-container">
              <el-input 
                v-model.trim="loginForm.captcha"
                placeholder="请输入验证码"
                class="captcha-input"
                :show-message="true"
                @focus="clearValidate('captcha')"
              >
              </el-input>
              <div class="captcha-code" @click="getCaptcha">
                <img v-if="captchaImage" :src="captchaImage" alt="验证码" />
              </div>
            </div>
          </el-form-item>
          <div class="remember-box">
            <el-checkbox v-model="rememberPassword">记住密码</el-checkbox>
          </div>
          <el-form-item>
            <el-button 
              type="primary" 
              class="login-button" 
              :loading="loading"
              @click="handleLogin">
              {{ loading ? '登录中...' : '登 录' }}
            </el-button>
          </el-form-item>
        </el-form>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import bgImage from '@/assets/images/keji.jpeg'  // 导入背景图片
import request from '@/api/request'  // 确保引入request
import { rsaEncrypt } from '@/utils/crypto'

const errorMessages = {
  'record not found': '登录名错误',
  'invalid password': '密码错误',
  'invalid captcha': '验证码错误',
  // ... 其他错误信息映射
}

export default {
  name: 'Login',
  data() {
    return {
      loginForm: {
        username: '',
        password: '',
        captcha: '',
        captchaId: ''  // 新增captchaId字段
      },
      rules: {
        username: [
          { required: true, message: '请输入登录名', trigger: 'blur' },
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
        ],
        captcha: [
          { required: true, message: '请输入验证码', trigger: 'blur' },
        ]
      },
      loading: false,
      rememberPassword: false,
      bgImage,  // 添加背景图片数据
      captchaImage: ''  // 新增验证码图片数据
    }
  },
  created() {
    // 测试加密
   
    this.getCaptcha()
  },
  methods: {
    ...mapActions('user', ['login']),
    
    // 获取验证码
    async getCaptcha() {
      try {
        const response = await request({
          url: '/api/v1/captcha',
          method: 'GET'
        })
        
        if (response.data && response.status === 1) {
          this.loginForm.captchaId = response.data.captchaId
          this.captchaImage = response.data.imageBase64
        } else {
          this.$message.error('获取验证码失败')
        }
      } catch (error) {
        console.error('获取验证码错误:', error)
        this.$message.error('获取验证码失败')
      }
    },
    
    // 添加清除单个字段验证的方法
    clearValidate(prop) {
      this.$refs.loginForm.clearValidate(prop)
    },
    
    // 修改登录方法
    async handleLogin() {
      try {
        await this.$refs.loginForm.validate()
        
        this.loading = true
        const loginData = {
          username: this.loginForm.username,
          password: rsaEncrypt(this.loginForm.password),
          captchaId: this.loginForm.captchaId,
          captcha: this.loginForm.captcha
        }
        
        try {
          const success = await this.login(loginData)
          if (success) {
            this.$message.success('登录成功')
            this.$router.push(this.$route.query.redirect || '/')
          }
        } catch (error) {
          if (error.response?.data?.msg) {
            const errorMsg = errorMessages[error.response.data.msg] || error.response.data.msg
            this.$message.error(errorMsg)
          } else {
            this.$message.error('登录失败，请重试')
          }
          
          // 登录失败时刷新验证码
          this.getCaptcha()
          // 清空验证码输入
          this.loginForm.captcha = ''
        }
      } catch (error) {
        // 处理表单验证错误
        if (error.errors) {
          const firstError = Object.values(error.errors)[0]
          if (firstError && firstError[0]) {
            this.$message.error(firstError[0])
          }
        }
        // 刷新验证码
        this.getCaptcha()
        // 清空验证码输入
        this.loginForm.captcha = ''
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.login-container {
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: url('../assets/images/keji.jpeg') center center no-repeat;
  background-size: cover;
  position: relative;


  .login-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 460px;
  }

  .login-box {
    width: 100%;
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(10px);
    border-radius: 15px;
    padding: 40px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.1);
    position: relative;
    z-index: 1;
  }

  .title {
    color: #fff;
    font-size: 28px;
    text-align: center;
    margin-bottom: 40px;
    font-weight: 500;
    text-shadow: 0 0 10px rgba(41, 196, 255, 0.5);
  }

  .custom-input :deep(.el-input__inner) {
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: #fff;
    height: 45px;
    transition: all 0.3s ease;
  }

  .custom-input :deep(.el-input__inner:focus) {
    border-color: #29c4ff;
    box-shadow: 0 0 10px rgba(41, 196, 255, 0.3);
  }

  .custom-input :deep(.el-input__prefix) {
    color: rgba(255, 255, 255, 0.7);
  }

  .captcha-container {
    display: flex;
    gap: 10px;
  }

  .captcha-input {
    flex: 1;
  }

  .captcha-code {
    width: 100px;
    height: 45px;
    background: rgba(41, 196, 255, 0.1);
    border: 1px solid rgba(41, 196, 255, 0.3);
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .captcha-code::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      45deg,
      transparent,
      rgba(41, 196, 255, 0.1),
      transparent
    );
    transform: rotate(45deg);
    animation: shine 2s infinite;
  }

  @keyframes shine {
    0% {
      transform: translateX(-100%) rotate(45deg);
    }
    100% {
      transform: translateX(100%) rotate(45deg);
    }
  }

  .remember-box {
    margin-bottom: 20px;
    color: #fff;
  }

  .login-button {
    width: 100%;
    height: 45px;
    background: linear-gradient(45deg, #29c4ff, #2d8cf0);
    border: none;
    font-size: 16px;
    position: relative;
    
    transition: all 0.3s ease;
  }

  .login-button:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(41, 196, 255, 0.3);
  }

  .login-button::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      45deg,
      transparent,
      rgba(255, 255, 255, 0.1),
      transparent
    );
    transform: rotate(45deg);
    animation: buttonShine 2s infinite;
  }

  @keyframes buttonShine {
    0% {
      transform: translateX(-100%) rotate(45deg);
    }
    100% {
      transform: translateX(100%) rotate(45deg);
    }
  }

  :deep(.el-checkbox__label) {
    color: #fff;
  }

  :deep(.el-checkbox__input.is-checked .el-checkbox__inner) {
    background-color: #29c4ff;
    border-color: #29c4ff;
  }

  :deep(.el-form-item__error) {
    color: #ff4949;
    font-size: 12px;
    line-height: 1;
    padding-top: 4px;
    position: absolute;
    top: 100%;
    left: 0;
    text-shadow: 0 0 5px rgba(0, 0, 0, 0.2);
  }

  .el-form-item {
    margin-bottom: 25px; // 增加间距以容纳错误提示
  }

  // 验证码输入框特殊处理
  .captcha-container {
    .el-form-item__error {
      bottom: -20px;
    }
  }

  // 输入框验证失败时的样式
  :deep(.el-form-item.is-error .el-input__inner) {
    border-color: #ff4949;
    box-shadow: 0 0 5px rgba(255, 73, 73, 0.3);
  }
}

.login-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4); /* 添加暗色遮罩，使内容更易读 */
  backdrop-filter: blur(3px); /* 轻微模糊背景 */
}
</style> 